import AsyncStorage from '@react-native-async-storage/async-storage';
import * as ImagePicker from 'expo-image-picker';
import { ScopedAsyncStorage } from '@/lib/storage';

const PROFILE_PICTURE_KEY = '@fimfam/profile-picture';

/**
 * ProfilePictureManager - Singleton
 * Manages profile picture storage locally (device only, no database sync).
 * Stores image URI in AsyncStorage for persistence across app restarts.
 */
export class ProfilePictureManager {
  private static instance: ProfilePictureManager;
  private currentPictureUri: string | null = null;
  private initialized = false;
  private storage: ScopedAsyncStorage | null = null;

  private constructor() {}

  /**
   * Get singleton instance
   */
  static getInstance(): ProfilePictureManager {
    if (!ProfilePictureManager.instance) {
      ProfilePictureManager.instance = new ProfilePictureManager();
    }
    return ProfilePictureManager.instance;
  }

  /**
   * Set scoped storage and reset initialization
   * Call this when user or device changes
   */
  setStorage(storage: ScopedAsyncStorage): void {
    this.storage = storage;
    this.initialized = false;
    this.currentPictureUri = null;
  }

  /**
   * Initialize by loading stored profile picture URI
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      const storedUri = this.storage
        ? await this.storage.getItem(PROFILE_PICTURE_KEY)
        : await AsyncStorage.getItem(PROFILE_PICTURE_KEY);
      this.currentPictureUri = storedUri;
      this.initialized = true;
    } catch (error) {
      console.error('[ProfilePictureManager:initialize] Failed to load profile picture:', error);
      this.currentPictureUri = null;
      this.initialized = true;
    }
  }

  /**
   * Get current profile picture URI
   */
  getProfilePictureUri(): string | null {
    return this.currentPictureUri;
  }

  /**
   * Request media library permissions
   */
  async requestPermissions(): Promise<boolean> {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    return status === 'granted';
  }

  /**
   * Pick image from device and save URI
   */
  async pickImage(): Promise<string | null> {
    try {
      // Request permissions
      const hasPermission = await this.requestPermissions();
      if (!hasPermission) {
        console.error('[ProfilePictureManager:pickImage] Permission denied');
        return null;
      }

      // Launch image picker
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (result.canceled) {
        return null;
      }

      // Save URI to storage
      const uri = result.assets[0].uri;
      await this.setProfilePictureUri(uri);
      return uri;
    } catch (error) {
      console.error('[ProfilePictureManager:pickImage] Failed to pick image:', error);
      throw error;
    }
  }

  /**
   * Set profile picture URI and persist to storage
   */
  async setProfilePictureUri(uri: string | null): Promise<void> {
    try {
      this.currentPictureUri = uri;
      if (uri) {
        if (this.storage) {
          await this.storage.setItem(PROFILE_PICTURE_KEY, uri);
        } else {
          await AsyncStorage.setItem(PROFILE_PICTURE_KEY, uri);
        }
      } else {
        if (this.storage) {
          await this.storage.removeItem(PROFILE_PICTURE_KEY);
        } else {
          await AsyncStorage.removeItem(PROFILE_PICTURE_KEY);
        }
      }
    } catch (error) {
      console.error('[ProfilePictureManager:setProfilePictureUri] Failed to save URI:', error);
      throw error;
    }
  }

  /**
   * Remove profile picture
   */
  async removeProfilePicture(): Promise<void> {
    await this.setProfilePictureUri(null);
  }
}
